import { z } from "zod";
import { PremiumTierSchema } from "./user.schemas.js";
/**
 * The player's premium tier and when it lapses. This is the **single** source
 * of a player's tier — it replaced a separate permanent `premiumTier` field
 * plus a time-limited "boost" (they were two representations of the same
 * thing). Every tier is time-limited: a permanent tier is simply one with a
 * far-future `expiresAt` (`PERMANENT_TIER_EXPIRES_AT`). Absent ⇒ `normal`.
 * Lives inside the server-owned `PlayerPremium` object below — never on the
 * client-writable `users/{uid}` doc, so a player can't self-grant a tier.
 */
export const PremiumTierGrantSchema = z.object({
    tier: PremiumTierSchema,
    /** Millisecond-epoch timestamp; the grant is inert once `expiresAt <= now`. */
    expiresAt: z.number().int().nonnegative(),
});
/**
 * Player-level premium state. Lives on the server-owned players doc
 * (`universes/{u}/players/{uid}.premium`) — never on the client-writable
 * `users/{uid}` doc: a player must not be able to self-grant currency
 * (`darkMatter`), their tier, or cosmetic unlocks. Every field defaults so a
 * partial/absent stored value still parses into a complete object.
 */
/**
 * Global (server-dictated, epoch-aligned — NOT a per-player rolling window)
 * counter for the Market's pirate-flight token-earning anti-farming
 * cap, which scales with the player's score and premium tier (see
 * `calculatePirateTokenDailyCap`, `packages/game-engine/src/trade.ts`, and
 * its backing constants in `packages/shared/src/constants/trade.ts`).
 * `windowStart` is a millisecond-epoch timestamp, same convention as
 * {@link PremiumTierGrantSchema.expiresAt} — see
 * `currentPirateTokenWindowStart`/`applyPirateTokenGrant` in
 * `packages/game-engine/src/trade.ts`.
 */
export const PirateTokenWindowSchema = z.object({
    windowStart: z.number().int().nonnegative(),
    count: z.number().int().nonnegative(),
});
export const PlayerPremiumSchema = z.object({
    /** Premium currency balance. Earned in-game (tutorial rewards + starter grant); real-money top-up is deferred. */
    darkMatter: z.number().int().nonnegative().default(0),
    /** The player's premium tier + expiry (see {@link PremiumTierGrantSchema}). Absent ⇒ `normal`. */
    tier: PremiumTierGrantSchema.optional(),
    /** Premium planet-image ids (7..`TOTAL_PLANET_IMAGE_COUNT`) the player owns. */
    unlockedPlanetImages: z.array(z.number().int()).default([]),
    /** Tutorial step indices whose reward has already been granted (double-claim guard). */
    claimedRewardSteps: z.array(z.number().int()).default([]),
    /** Market trade-token balance — spent by `/tradeResources`, earned via a won Pirate flight (capped, see `pirateTokenWindow`) or bought via `/purchaseTradeTokens`. */
    tradeTokens: z.number().int().nonnegative().default(0),
    /** Anti-farming cap window for pirate-earned trade tokens — see {@link PirateTokenWindowSchema}. Absent ⇒ no tokens earned yet in the current window. */
    pirateTokenWindow: PirateTokenWindowSchema.optional(),
});
/**
 * Normalize a raw (possibly absent/partial, e.g. an older doc predating a
 * newly-added field) premium value into a complete `PlayerPremium`. The one
 * canonical parse used by both the server (`readPlayerPremium` in
 * `apps/functions/src/advance.ts`, wrapping this with the player-doc shape)
 * and every client service that reads a `premium` field off an endpoint
 * response — previously duplicated as `PlayerPremiumSchema.parse(x ?? {})`
 * at each call site.
 */
export function parsePlayerPremium(raw) {
    return PlayerPremiumSchema.parse(raw ?? {});
}
/** Buy `quantity` trade tokens for `quantity * TRADE_TOKEN_PRICE` Dark Matter. */
export const PurchaseTradeTokensSchema = z.object({
    quantity: z.number().int().positive(),
});
/** Buy one Emergency Ozone for the given (ozone-locked) planet. */
export const PurchaseEmergencyOzoneSchema = z.object({
    planetId: z.string(),
});
/** Buy a time-limited premium tier from the `PREMIUM_BOOST_PRODUCTS` catalog. */
export const PurchasePremiumBoostSchema = z.object({
    productId: z.string(),
});
/** Permanently unlock a premium planet image (id above `FREE_PLANET_IMAGE_COUNT`). */
export const UnlockPlanetImageSchema = z.object({
    planetType: z.number().int(),
});
/**
 * Claim the reward attached to a completed tutorial step (idempotent
 * server-side). `planetId` is the planet a `resources`/`ships` reward is
 * credited to, and whose state the step's completion is checked against.
 */
export const ClaimTutorialRewardSchema = z.object({
    stepIndex: z.number().int().min(0),
    planetId: z.string().min(1).optional(),
});
/**
 * Buy one Resource Pack for the given planet (`RESOURCE_PURCHASE_PRICE` Dark
 * Matter, capped at `RESOURCE_PURCHASE_DAILY_LIMIT` per planet per rolling
 * 24h window — see `Planet.resourcePurchaseDaily`).
 */
export const PurchaseResourcesSchema = z.object({
    planetId: z.string(),
});
//# sourceMappingURL=premium.schemas.js.map